import type { ReactNode } from 'react'
import { motion } from 'motion/react'
import { useLocation } from 'react-router-dom'

interface PageTransitionProps {
  children: ReactNode
}

/**
 * Fades each route in as it mounts.
 *
 * The purpose is spatial continuity: without it a click swaps the entire
 * screen in one frame and the visitor has to re-orient.
 *
 * There is deliberately **no exit animation and no `AnimatePresence`**. An
 * earlier version used `mode="wait"`, which holds the incoming page back
 * until the outgoing one has finished leaving — that put a measured ~300ms
 * of blank screen between every navigation and made pages feel like they
 * were not showing up. Keying on `pathname` remounts immediately, so the new
 * page starts painting on the same frame the click lands.
 *
 * Kept on `pathname` only, so changing a filter's query string does not
 * re-mount and re-animate the page underneath it.
 */
export const PageTransition = ({ children }: PageTransitionProps) => {
  const { pathname } = useLocation()

  return (
    <motion.div
      key={pathname}
      initial={{ opacity: 0, transform: 'translateY(6px)' }}
      animate={{ opacity: 1, transform: 'translateY(0px)' }}
      transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
    >
      {children}
    </motion.div>
  )
}
